/**
 * Condução e estado da via, a partir da tag `0x47`.
 *
 * ## A limitação que precisa estar visível, não escondida
 *
 * O equipamento reporta o **pico entre um registro e outro**, e a janela é de
 * ~30 s. Três freadas bruscas viram um número só, igual a uma. Por isso aqui
 * **não existe contagem de eventos** — existe severidade por janela.
 *
 * Qualquer "ranking de frenagens bruscas" construído contando registros acima de
 * um limiar estaria errado, e pareceria certo. Se um dia isso for pedido, o
 * caminho é baixar o intervalo de gravação, não reinterpretar este número.
 *
 * ## Os limiares são de transporte coletivo, não de automóvel
 *
 * Painel de frota de carros usa 0,25–0,35 g como "brusco". Ônibus leva gente EM
 * PÉ: a literatura de conforto em transporte público trabalha entre 0,11 e
 * 0,15 g para aceleração e frenagem não-emergenciais, e o critério lateral para
 * passageiro em pé fica em torno de 1,5 m/s² (~0,15 g).
 *
 * Medido no parque em 2026-09-03: p99 de frenagem em 0,21 g e de aceleração em
 * 0,16 g — ou seja, o percentil 99 já está acima do conforto. É esperado em
 * operação urbana, e é a razão de o limiar ser calibrável e não cravado.
 */
import type { TelemetryRow } from "./schemas.ts";

/** Faixa confortável em transporte coletivo, em centésimos de g. */
export const COMFORT_CG = 15;

/** Acima disto, desconforto claro para quem está em pé. Calibrável. */
export const HARSH_CG = 25;

/**
 * Solavanco que vale marcar no mapa como via ruim.
 *
 * Medido: 70% dos registros têm 0 e o p99 fica em 37. Em 60 só sobra o que
 * realmente sacode — abaixo disso o mapa vira mancha e não aponta nada.
 */
export const ROAD_BUMP_CG = 60;

export type EcoAxis = "accel" | "brake" | "corner" | "bump";

export interface AxisSummary {
  readonly axis: EcoAxis;
  /** Texto para o usuário. */
  readonly label: string;
  readonly p95Cg: number | null;
  readonly maxCg: number | null;
  /** Quantas janelas passaram do conforto — janelas, NÃO eventos. */
  readonly windowsOverComfort: number;
  readonly windows: number;
}

const FIELD: Record<EcoAxis, keyof TelemetryRow> = {
  accel: "eco_accel_cg",
  brake: "eco_brake_cg",
  corner: "eco_corner_cg",
  bump: "eco_bump_cg",
};

const LABEL: Record<EcoAxis, string> = {
  accel: "Aceleração",
  brake: "Frenagem",
  corner: "Lateral",
  bump: "Solavanco",
};

export function summarizeAxis(rows: readonly TelemetryRow[], axis: EcoAxis): AxisSummary {
  const values = rows
    .map((row) => row[FIELD[axis]])
    .filter((value): value is number => typeof value === "number");

  if (values.length === 0) {
    return { axis, label: LABEL[axis], p95Cg: null, maxCg: null, windowsOverComfort: 0, windows: 0 };
  }

  const sorted = [...values].sort((a, b) => a - b);
  return {
    axis,
    label: LABEL[axis],
    // p95 e não média: a média de um pico por janela dilui justamente o que
    // interessa, porque a maioria das janelas é tranquila.
    p95Cg: sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))] ?? null,
    maxCg: sorted[sorted.length - 1] ?? null,
    windowsOverComfort: values.filter((value) => value > COMFORT_CG).length,
    windows: values.length,
  };
}

/** Os três eixos de condução. O solavanco fica de fora: ele é via, não motorista. */
export function summarizeDriving(rows: readonly TelemetryRow[]): AxisSummary[] {
  return (["accel", "brake", "corner"] as const).map((axis) => summarizeAxis(rows, axis));
}

export type Severity = "calm" | "firm" | "harsh";

export function severityOf(cg: number | null): Severity {
  if (cg === null || cg <= COMFORT_CG) return "calm";
  return cg >= HARSH_CG ? "harsh" : "firm";
}

export const SEVERITY_STYLE: Record<Severity, { bar: string; text: string; label: string }> = {
  calm: { bar: "bg-emerald-500", text: "text-emerald-700 dark:text-emerald-300", label: "confortável" },
  firm: { bar: "bg-amber-500", text: "text-amber-700 dark:text-amber-300", label: "firme" },
  harsh: { bar: "bg-rose-500", text: "text-rose-700 dark:text-rose-300", label: "desconfortável" },
};

export interface RoadPoint {
  readonly lat: number;
  readonly lon: number;
  readonly bumpCg: number;
  readonly at: string;
}

/**
 * Pontos de via ruim: solavanco forte E com posição.
 *
 * Sem coordenada o ponto não serve para mapa — e registro sem posição existe
 * (equipamento sem sinal de GPS ainda manda telemetria).
 */
export function roadPoints(rows: readonly TelemetryRow[], minCg = ROAD_BUMP_CG): RoadPoint[] {
  const points: RoadPoint[] = [];
  for (const row of rows) {
    const bump = row.eco_bump_cg;
    if (typeof bump !== "number" || bump < minCg) continue;
    if (typeof row.lat !== "number" || typeof row.lon !== "number") continue;
    points.push({ lat: row.lat, lon: row.lon, bumpCg: bump, at: row.time });
  }
  return points;
}

/** Centésimos de g para o texto do usuário: `0,21 g`. */
export function formatG(cg: number | null): string {
  return cg === null ? "—" : `${(cg / 100).toFixed(2).replace(".", ",")} g`;
}
